/**
 * API service functions for managing SIM results, scripts, screen share streams,
 * and user result data via the RLC backend.
 *
 * @module services/result.service
 * @author Snowfly
 * @version 1.0.0
 */
import axios from 'axios';
import config from '../config';
import sims from '../permissions/sims';
import admin from '../permissions/admin';
import dashboard from '../permissions/dashboard';
import helpdesk from '../permissions/helpdesk';

/**
 * Maximum size (in bytes) for a single upload chunk sent to the server.
 *
 * @type {number}
 */
export const MAX_CHUNK_SIZE = 512*1024; // 512KB

/**
 * Constructs and returns an object containing headers for a request.
 *
 * @function getHeader
 * @param {string} task - The task name or identifier.
 * @param {string} type - The type or category of the action.
 * @returns {object} An object with a `headers` property that includes:
 * - `Authorization`: A token retrieved via the `config.getToken` method.
 * - `task`: The provided task parameter.
 * - `type`: The provided type parameter.
 */
const getHeader = (task, type) => ({
    headers: {
        Authorization: config.getToken(),
        task,
        type
    },
    withCredentials: false,
});

/**
 * Sends a POST request to add a new level script.
 *
 * @param {Object} data - The data of the level script to be added.
 * @returns {Promise<Object>} A promise that resolves to the response data of the API request.
 *
 * The function utilizes Axios to send a POST request to the endpoint defined in
 * `config.API_BASE_URL`, appending `/api/v1/sims/scripts` to its path. It includes
 * headers generated using the `getHeader` function.
 */
export const addLevelScript = async (data) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/scripts`,
        data,
        getHeader(null, null)
    ).then((res) => res.data);
};

/**
 * Sends a POST request to save keywords to the API endpoint.
 *
 * @async
 * @function saveKeywords
 * @param {Object} data - The payload containing keywords to be saved.
 * @returns {Promise} A promise that resolves to the response of the POST request.
 * @throws Will throw an error if the request fails.
 */
export const saveKeywords = async (data) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/keywords`,
        data,
        getHeader(null, null),
    );
};

/**
 * Updates the avatar details on the server using an HTTP PATCH request.
 *
 * @async
 * @function saveAvatarDetails
 * @param {Object} data - The data containing avatar details to be updated.
 * @returns {Promise<Object>} - A promise that resolves to the server's response data.
 */
export const saveAvatarDetails = async (data) => {
    return axios
        .patch(
            `${config.API_BASE_URL}/api/v1/sims/avatars/update-details`,
            data,
            getHeader(null, null),
        )
        .then((res) => res.data);
};

/**
 * Updates the level information for a specific ID.
 *
 * @async
 * @function editLevels
 * @param {string} id - The unique identifier of the level to be updated.
 * @param {Object} data - The data object containing the updated information for the level.
 * @returns {Promise} A Promise that resolves to the response of the PATCH request.
 */
export const editLevels = async (id, data) => {
    return axios.patch(
        `${config.API_BASE_URL}/api/v1/sims/editLevel/${id}`,
        data,
        getHeader(null, null),
    );
};

/**
 * Asynchronously uploads audio data for transcription grading.
 * Sends a POST request to the specified API endpoint with the provided data.
 *
 * @async
 * @function uploadAudio
 * @param {Object} data - The audio data to be uploaded for transcription grading.
 * @returns {Promise<Object>} A promise that resolves to the response from the API
 *                            containing the transcription grade or status.
 * @throws {Error} Throws an error if the API request fails.
 */
export const uploadAudio = async (data) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/grade-transcription`,
        data,
        getHeader(null, null),
    );
};

/**
 * Sends an HTTP POST request to delete a script using the provided data.
 *
 * @function deleteScript
 * @async
 * @param {Object} data - The payload containing the details of the script to be deleted.
 * @returns {Promise} Resolves with the response from the server or rejects with an error.
 */
export const deleteScript = async (data) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/delete-script`,
        data,
        getHeader(null, null),
    );
};

/**
 * Sends a POST request to generate an audio file based on the provided data.
 * This function communicates with the voice generation endpoint
 * to create a voice simulation using the given input.
 *
 * @async
 * @function
 * @param {Object} data - The input data required for audio generation.
 * @returns {Promise<Object>} A promise that resolves with the voice generation result from the server.
 * @throws {Error} Throws an error if the request fails or the response is invalid.
 */
export const generateAudio = async (data) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/generate-voice`,
        data,
        getHeader(null, null),
    ).then((res) => res.data);
};

/**
 * Fetches a list of SIMs for a given product ID from the API.
 * Makes an HTTP GET request to retrieve SIM information associated with the specified product.
 *
 * @async
 * @param {string} id - The product ID for which the SIMs need to be retrieved.
 * @returns {Promise<Object>} A promise that resolves to the response object containing the SIM list data.
 * @throws {Error} If the request fails or encounters an error.
 */
export const getSims = async (id) => {
    return axios.get(
        `${config.API_BASE_URL}/api/v1/sims/list?product_id=${id}`,
        getHeader(null, null)
    );
};

/**
 * Retrieves SIM card details by its unique identifier.
 *
 * This asynchronous function sends a GET request to the specified API endpoint
 * to fetch details of a SIM card based on the provided `id`.
 *
 * @async
 * @function
 * @param {string} id - The unique identifier of the SIM card.
 * @returns {Promise<Object>} A promise that resolves to the response data containing SIM card details.
 * @throws {Error} Throws an error if the request fails or the API returns an error response.
 */
export const getSimById = async (id) => {
    return axios.get(
        `${config.API_BASE_URL}/api/v1/sims/details?simId=${id}`,
        getHeader(null, null),
    );
};

/**
 * Sends a POST request to add a new level to the system.
 *
 * The function uses the configured API base URL and the endpoint
 * `/api/v1/sims/add-level` to send the request. The request includes
 * the provided data as the request body and standard headers created
 * by the `getHeader` function.
 *
 * @async
 * @function addLevel
 * @param {Object} data - The payload containing the level details to be added.
 * @returns {Promise<Object>} A promise that resolves to the response
 * from the server.
 * @throws {Error} If the HTTP request fails or encounters an error.
 */
export const addLevel = async (data) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/add-level`,
        data,
        getHeader(null, null),
    );
};

/**
 * Sends an HTTP POST request to delete a level using the specified data.
 * The API endpoint is defined in the `config.API_BASE_URL` and appends
 * the path `/api/v1/sims/delete-level`.
 * Implements headers using the `getHeader` function.
 *
 * @function
 * @async
 * @param {Object} data - The data payload to be sent in the POST request.
 * @returns {Promise<Object>} A promise that resolves to the response of the API call.
 * @throws {Error} Throws an error if the request fails or the server responds with an error.
 */
export const deleteLevel = async (data) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/delete-level`,
        data,
        getHeader(null, null),
    );
};

/**
 * Deletes a SIM by sending a POST request to the server.
 *
 * @function deleteSim
 * @async
 * @param {Object} data - The data to be sent in the request body for deleting the SIM.
 * @returns {Promise} Resolves with the server response after attempting deletion.
 */
export const deleteSim = async (data) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/delete`,
        data,
        getHeader(sims.sim_list.delete.task, sims.sim_list.delete.type),
    );
};

/**
 * Sends a POST request to duplicate a SIM record.
 *
 * The function sends an HTTP POST request to the duplicate SIM API endpoint
 * using the provided data. It utilizes Axios for making the request and
 * includes the necessary headers for authentication or any other configurations.
 *
 * @param {Object} data - The data to be sent in the body of the request.
 *                         This typically contains the information required
 *                         for duplicating a SIM record.
 * @returns {Promise<Object>} - A Promise that resolves to the response from
 *                              the API. The response may contain the duplicated
 *                              SIM data or a status indicating the result of
 *                              the operation.
 * @throws {Error} - Throws an error if the HTTP request fails or if the API
 *                   returns an error status.
 */
export const duplicateSim = async (data) => axios.post(
    `${config.API_BASE_URL}/api/v1/sims/duplicate-sim`,
    data,
    getHeader(null, null)
);

/**
 * Fetches the missed correct keywords for a given identifier.
 *
 * @async
 * @function getMissedCorrectKeywords
 * @param {string} id - The unique identifier associated with the request.
 * @returns {Promise<Object>} A promise that resolves to the response data containing the missed correct keywords.
 * @throws {Error} Throws an error if the request fails or if the response contains an error.
 */
export const getMissedCorrectKeywords = async (id) => axios.get(
    `${config.API_BASE_URL}/api/v1/sims/get-missed-correct-keywords?id=${id}`,
    getHeader(null, null)
);

/**
 * Deletes a role from the system by sending a POST request to the delete-role API endpoint.
 *
 * @async
 * @function deleteRole
 * @param {string} id - The unique identifier of the role to be deleted.
 * @returns {Promise} A Promise resolving to the response of the API call.
 * @throws {Error} Throws an error if the API call fails.
 */
export const deleteRole = async (id) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/company/delete-role`,
        id,
        getHeader(admin.roles.delete.task, admin.roles.delete.type)
    );
};

/**
 * Asynchronously deletes a group by its unique identifier (ID).
 *
 * This function sends a POST request to the API endpoint responsible for deleting groups.
 * It constructs the request using a base URL, the provided group ID, and authorization headers.
 *
 * @param {string} id - The unique identifier of the group to be deleted.
 * @returns {Promise<Object>} A promise that resolves to the response object from the API.
 * @throws {Error} If an error occurs during the API request.
 */
export const deleteGroup = async (id) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/groups/delete`,
        id,
        getHeader(admin.groups.delete.task, admin.groups.delete.type)
    );
};

/**
 * Asynchronously uploads the recorded audio to the API endpoint.
 *
 * This function sends a POST request to upload the provided audio data to the specified service URL.
 * The audio data must comply with the requirements of the API, and appropriate headers will be included in the request.
 *
 * @param {Object} data - The audio data to be uploaded. This should contain the relevant audio file and any necessary metadata.
 * @returns {Promise} - A promise that resolves with the server response or rejects with an error.
 */
export const uploadRecordedAudio = async (data) => axios.post(
    `${config.API_BASE_URL}/api/v1/sims/upload-audio`,
    data,
    getHeader(null, null)
);

/**
 * Fetches the list of users belonging to a specific group by making an API request.
 *
 * @async
 * @function fetchUserByGroup
 * @param {string} id - The unique identifier of the group.
 * @returns {Promise<Object>} A promise that resolves to the response of the GET request.
 *                             This response typically contains the list of users in the specified group.
 * @throws {Error} Throws an error if the API request fails.
 */
export const fetchUserByGroup = async (id) => axios.get(
    `${config.API_BASE_URL}/api/v1/users/user-by-group?group_id=${id}`,
    getHeader(null, null)
);

/**
 * Handles saving assessor notes to the server. The request is made via a POST request
 * to the specified endpoint, including the necessary data and authentication headers.
 * The behavior of the request is determined by the `forUpdate` parameter, which decides
 * between creating new notes or updating existing ones.
 *
 * @param {string} id - The unique identifier associated with the entity for which notes are being saved.
 * @param {Object} notes - The notes data object to be sent in the request body.
 * @param {boolean} forUpdate - A flag specifying whether the operation is an update
 *                              (true) or creation (false) of assessor notes.
 * @returns {Promise<Object>} A promise that resolves to the response data obtained from the server.
 */
export const SaveAssessorNotes = async (id, notes, forUpdate) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/save-assessor-notes/${id}`,
        notes,
        getHeader(
            forUpdate ?
                dashboard.assessor_notes.update.task :
                dashboard.assessor_notes.create.task,
            forUpdate ?
                dashboard.assessor_notes.update.type :
                dashboard.assessor_notes.create.type,
        ),
    )
    .then((res) => res.data);
};

/**
 * Asynchronously checks whether a user result exists in the system.
 *
 * This function sends a POST request to the configured API endpoint to verify
 * the existence of a user's result. The existence is determined based on the
 * data provided in the payload and optionally by the user ID.
 *
 * @param {Object} payload - The data to be sent in the request body, used to
 *                           determine the existence of a user's result.
 * @param {string} [user_id] - Optional user identifier to be included in the
 *                             request query parameters.
 * @returns {Promise<boolean>} A promise that resolves to a boolean indicating
 *                             whether the user's result exists. Returns `false`
 *                             if the request fails or the result does not exist.
 */
export const checkUserResultExist = async (payload, user_id) => {
    let userArg = user_id ? `&user_id=${user_id}` : '';
    return axios({
        url: `${config.API_BASE_URL}/api/v1/sims/result?check_result_exist=true${userArg}`,
        method: 'POST',
        ...getHeader(null, null),
        data: payload,
    }).then((res) => res.data.result_exist)
        .catch(() => false);
};

/**
 * Fetches all assignments from the API with the specified parameters.
 *
 * @param {boolean} myAssignments - Indicates if user-specific assignments should be fetched.
 * @param {string} tab - Specifies the tab filter for assignments (optional).
 * @param {string} textSearch - Specifies a status to filter assignments by (optional).
 * @param {string} task - A task identifier used for headers.
 * @param {string} type - A type identifier used for headers.
 * @returns {Promise} A promise that resolves to the response containing assignment data.
 */
export const fetchAllAssignments = async (myAssignments, tab, textSearch, task, type) => {
    return axios.get(
        `${config.API_BASE_URL}/api/v1/sims/excel/assignments?${textSearch ? `&status=${textSearch}` : ''}&my_assignment=${myAssignments}${tab ? `&tab=${tab}` : ''}`,
        {
            ...getHeader(task, type),
            responseType: 'arraybuffer',
        }
    );
};

/**
 * Fetches score lists based on the provided parameters.
 *
 * @async
 * @function fetchScoreLists
 * @param {string} textSearch - A search query for filtering the results by status.
 * @param {string} finalResult - A query parameter to filter the results by final result.
 * @param {string} download - A flag to request downloadable data.
 * @param {Object} filterDatas - Additional filter criteria sent in the request body.
 * @returns {Promise<AxiosResponse>} A Promise that resolves to the response from the server containing the score lists data.
 */
export const fetchScoreLists = async (textSearch, finalResult, download, filterDatas) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/level/listing?${textSearch ? "&status="+textSearch : ''}${finalResult ? "&final_result="+finalResult : ''}${download ? "&download="+download : ''}`,
        filterDatas,
        {
            ...getHeader(null, null),
            responseType: 'arraybuffer',
        }
    );
};

/**
 * Asynchronously downloads a template file from the server.
 *
 * This function sends a GET request to a specific API endpoint to retrieve a template file.
 * The response is expected to be in the format of an arraybuffer. The API endpoint is constructed
 * using a base URL defined in the configuration.
 *
 * @function
 * @async
 * @returns {Promise<AxiosResponse<Uint8Array>>} A promise that resolves to the server response containing the template file as an arraybuffer.
 */
export const downloadTemplate = async () => {
    return axios.get(`${config.API_BASE_URL}/api/v1/sims/download-template`,
        {
            ...getHeader(null, null),
            responseType: 'arraybuffer',
        }
    );
};

/**
 * Performs a duplicate email check by sending a GET request to the API.
 *
 * @async
 * @function
 * @param {string} email - The email address to be checked for duplication.
 * @param {string} [id] - Optional user ID to exclude from the duplicate check.
 * @returns {Promise} A promise resolving with the response from the API.
 */
export const duplicateEmailCheck = async (email, id) => {
    return axios.get(
        `${config.API_BASE_URL}/api/v1/users/duplicate-email?emailId=${email}${id ? `&id=${id}` : ''}`,
        getHeader(null, null),
    );
};

/**
 * An asynchronous function to save permissions for a specific role or entity.
 *
 * @param {Object} data - The payload containing the permission data to be saved.
 * @param {Object} header - An object containing header information, including task and type.
 * @returns {Promise} A Promise that resolves with the response from the API.
 *                    The response typically contains the status and data related
 *                    to the save permissions request.
 *                    If the API call fails, the Promise will reject with an error.
 */
export const savePermission = async (data, header) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/roles/save-permissions`,
        data,
        getHeader(header.task, header.type),
    );
};

/**
 * Retrieves a pre-signed stream-transcription-websocket URL from the AWS API.
 *
 * This asynchronous function sends a GET request to fetch a pre-signed URL
 * from the API endpoint. The function utilizes the base API URL from the
 * configuration and dynamically constructs the API call. Headers for the
 * request are provided by the `getHeader` method.
 *
 * @async
 * @function getAwsPresignedUrl
 * @returns {Promise<Object>} A Promise that resolves to the response object containing the pre-signed URL.
 * @throws {Error} If the request fails or the server returns an error.
 */
export const getAwsPresignedUrl = async () => {
    return axios.get(
        `${config.API_BASE_URL}/api/v1/presigned/url`,
        getHeader(null, null),
    );
};

/**
 * Asynchronously uploads a recorded video to the server.
 *
 * This function utilizes an HTTP POST request to send the provided video data
 * to the specific API endpoint for video uploads. The necessary headers
 * for the request are dynamically generated and included.
 *
 * @function
 * @async
 * @param {Object} data - The video data to be uploaded, typically in a format
 *                         supported by the server (e.g., FormData).
 * @returns {Promise<Object>} A promise that resolves to the HTTP response
 *                            from the server after uploading the video.
 * @throws {Error} Throws an error if the HTTP request fails.
 */
export const uploadRecordedVideo = async (data) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/upload-video`,
        data,
        getHeader(null, null),
    );
};

/**
 * Assigns a temporary role to a specified user.
 *
 * This asynchronous function sends a POST request to the API to assign a temporary role.
 * The API request includes the user's ID and role data in the payload.
 *
 * @param {Object} data - The role data to be assigned, including details of the temporary role.
 * @param {string|number} user - The identifier (ID) of the user to whom the temporary role will be assigned.
 * @returns {Promise<Object>} - A Promise that resolves to the response from the API after assigning the temporary role.
 * @throws {Error} - Throws an error if the API request fails.
 */
export const AssignTemporaryRole = async (data, user) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/roles/add-temp-role?user_id=${user}`, data,
        getHeader(admin.roles.update.task, admin.roles.update.type),
    );
};

/**
 * Deletes a temporary role for the specified user.
 *
 * This asynchronous function sends a POST request to the API endpoint to remove a temporary role
 * assigned to a user. It utilizes the configured API base URL and includes the appropriate request headers.
 *
 * @param {string} role - The temporary role to be deleted for the user.
 * @param {string} user - The user ID for which the temporary role should be removed.
 * @returns {Promise} A promise that resolves with the response from the API.
 */
export const DeleteTempRole = async (role, user) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/roles/delete-temp-role?user_id=${user}`, {role},
        getHeader(null, null),
    );
};

/**
 * Downloads an Excel file for a specific level by its ID.
 *
 * This function sends an asynchronous GET request to the server to retrieve
 * an Excel file associated with the provided level ID. The response is
 * expected to be in the form of an array buffer.
 *
 * @param {string} levelId - The unique identifier of the level for which the Excel file is to be downloaded.
 * @returns {Promise} - A promise that resolves to the response of the GET request, containing the Excel file as an array buffer.
 */
export const downloadExcelLevel = async (levelId) => {
    return axios.get(
        `${config.API_BASE_URL}/api/v1/sims/excel/levels?level_id=${levelId}`,
        {
            ...getHeader(),
            responseType: 'arraybuffer',
        }
    );
};

/**
 * Function to send a POST request to continue a simulation level.
 *
 * @param {string} simId - The unique identifier for the simulation.
 * @param {string} simLevelId - The unique identifier for the simulation level.
 * @param {string} mode - The mode of the simulation (e.g., practice or test mode).
 * @returns {Promise} A promise that resolves with the response of the API call.
 */
export const checkContinueLevel = async (simId, simLevelId, mode) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/continue`,
        {simId, simLevelId, mode},
        getHeader(null, null),
    );
};

/**
 * Asynchronously retrieves a user script ID from the API.
 *
 * This function sends a POST request to fetch the script ID associated with
 * a user response and simulation script. It uses `axios` for the HTTP request,
 * and includes a custom header configuration.
 *
 * @async
 * @function
 * @param {string} user_response_id - The ID representing the user's response.
 * @param {string} sim_script_id - The ID representing the simulation script.
 * @returns {Promise<Object>} A promise that resolves to the response containing the user script ID.
 * @throws {Error} Throws an error if the HTTP request fails or the API returns an error.
 */
export const getUserScriptId = async (user_response_id, sim_script_id) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/user-script-id`,
        {user_response_id, sim_script_id},
        getHeader(null, null),
    );
};

/**
 * Handles the process of initiating a password reset for a user.
 * Sends a POST request to the server with the provided payload.
 *
 * @async
 * @function forgotPassword
 * @param {Object} payload - The data to be sent in the request body, typically includes user information required for password reset.
 * @returns {Promise<void>} Resolves when the request is completed successfully, or rejects with an error.
 */
export const forgotPassword = async (payload) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/login/forgot-password`,
        payload,
        getHeader(null, null),
    );
};

/**
 * Sends a reset password request to the server.
 *
 * @async
 * @function resetPassword
 * @param {Object} payload - The data required to reset the password, typically including user-specific reset details.
 * @returns {Promise<void>} Resolves when the request is successfully sent.
 * @throws {Error} Throws an error if the request fails.
 */
export const resetPassword = async (payload) => {
    axios.post(
        `${config.API_BASE_URL}/api/v1/login/reset-password`,
        payload,
        getHeader(null, null),
    );
};

/**
 * Asynchronously removes a PDF document associated with a specific SIM.
 *
 * @param {string} simId - The unique identifier of the SIM to which the document is linked.
 * @param {string} documentId - The unique identifier of the document to be removed.
 * @returns {Promise<Object>} A promise representing the HTTP response from the API.
 */
export const removePdf = async (simId, documentId) => {
    return axios.patch(
        `${config.API_BASE_URL}/api/v1/sims/remove-document`,
        {simId, documentId},
        getHeader(null, null),
    );
};

/**
 * Sends data to the real-time feedback endpoint of the API and retrieves the response.
 *
 * This function performs an HTTP POST request to the designated API endpoint for real-time feedback
 * using the provided data. The result of the API call is returned after being processed.
 *
 * @async
 * @function
 * @param {Object} data - The data to be sent in the body of the POST request.
 * @returns {Promise<Object>} A promise resolving to the response data from the API.
 */
export const realTimeFeedback = async (data) => {
    return axios
        .post(
            `${config.API_BASE_URL}/api/v1/sims/scripts/real-time-feedback`,
            data,
            getHeader(null, null),
        )
        .then((res) => res.data);
};

/**
 * Updates the SendToSnowfly flag for the passed SIM levels
 * @param data
 *
 * @returns {Promise<AxiosResponse<any>>}
 */
export const sendToSnowfly = async (data) => {
    return axios
        .post(
            `${config.API_BASE_URL}/api/v1/sims/send-to-snowfly`,
            data,
            getHeader(null, null),
        )
        .then((res) => res.data);
};

/**
 * Updates the toggleScreenRecordingSwitch flag for the passed SIM levels
 * @param data
 * @returns {Promise<AxiosResponse<any>>}
 */
export const toggleScreenRecordingSwitch = async (data) => {
    return axios
        .post(
            `${config.API_BASE_URL}/api/v1/sims/toggle-screen-recording-switch`,
            data,
            getHeader(null, null),
        )
        .then((res) => res.data);
};

/**
 * Toggles the practice mode switch by making a POST request to the server.
 *
 * @param {Object} data - The payload to be sent in the request body.
 * @returns {Promise<Object>} A promise that resolves to the response data from the server.
 */
export const togglePracticeModeSwitch = async (data) => {
    return axios
        .post(
            `${config.API_BASE_URL}/api/v1/sims/toggle-practice-mode-switch`,
            data,
            getHeader(null, null),
        )
        .then((res) => res.data);
};

/**
 * Toggles the autoplay mode switch for a given set of data.
 *
 * This asynchronous function sends a POST request to the server to update
 * the autoplay mode status, using the provided data payload. The function
 * expects the server to return a response containing the updated data.
 *
 * @param {Object} data - The payload sent in the POST request. This should
 *   include the necessary information for toggling the autoplay mode.
 * @returns {Promise<Object>} A promise that resolves to the response data
 *   from the server.
 * @throws {Error} If the request fails, the error will be thrown, including
 *   details about the failure.
 */
export const toggleAutoplayModeSwitch = async (data) => {
    return axios
        .post(
            `${config.API_BASE_URL}/api/v1/sims/toggle-autoplay-mode-switch`,
            data,
            getHeader(null, null),
        )
        .then((res) => res.data);
};

/**
 * Deletes a specific employee user from the system by ID.
 *
 * @function DeleteUserEmployee
 * @async
 * @param {string} id - The unique identifier of the employee user to be deleted.
 * @returns {Promise<Object>} A promise that resolves with the response from the server.
 * @throws {Error} Throws an error if the deletion request fails or the server returns an error response.
 */
export const DeleteUserEmployee = async (id) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/employee/delete`,
        {id},
        getHeader(admin.employee_list.delete.task, admin.employee_list.delete.type),
    );
};

/**
 * Fetches SIMs data for a specified product ID.
 *
 * This asynchronous function sends a GET request to the API endpoint to retrieve
 * SIMs data associated with the provided product ID. It utilizes the `axios` library
 * for HTTP communication and includes request headers generated by the `getHeader` function.
 *
 * @async
 * @function
 * @param {string|number} id - The unique identifier of the product for which SIMs data is requested.
 * @returns {Promise<Object>} A promise that resolves to the response object containing SIMs data.
 * @throws {Error} If the HTTP request fails or encounters an error.
 */
export const getSimsData = async (id) => {
    return axios.get(
        `${config.API_BASE_URL}/api/v1/sims/simData?product_id=${id}`,
        getHeader(null, null)
    );
};

/**
 * Asynchronously updates the hint text for a given resource by making a POST request to the specified API endpoint.
 *
 * @param {object} data - The data to be sent in the body of the POST request.
 * @returns {Promise<object>} A promise that resolves to the response data from the API.
 */
export const updateHintText = async (data) => {
    return axios
        .post(
            `${config.API_BASE_URL}/api/v1/sims/scripts/hint-text`,
            data,
            getHeader(null, null),
        )
        .then((res) => res.data);
};

/**
 * Sends a POST request to update the keyword score.
 *
 * This function uses the Axios library to send a POST request to the API endpoint
 * responsible for updating keyword scores. The endpoint URL is constructed using
 * the base API URL from the configuration. Additional headers required for the request
 * are generated using the `getHeader` function.
 *
 * @async
 * @function
 * @param {Object} data - The data to be sent in the*/
export const updateKeywordScore = async (data) => {
    return axios
        .post(
            `${config.API_BASE_URL}/api/v1/sims/levels/keyword-score`,
            data,
            getHeader(null, null),
        )
        .then((res) => res.data);
};

/**
 * Updates the exited level result for a given ID with the provided data.
 * Sends a PATCH request to the designated API endpoint to modify
 * the exited level result record.
 *
 * @param {string} id - The unique identifier for the exited level result to be updated.
 * @param {Object} [data={}] - The data to be updated for the exited level result. Defaults to an empty object.
 */
export const deleteExitedLevelResult = async (id, data = {}) => {
    return axios.patch(
        `${config.API_BASE_URL}/api/v1/sims/update-exited-level-result/${id}`,
        data,
        getHeader(null, null),
    );
};

/**
 * Fetches the assignment history for a given assignment ID.
 * The function sends a GET request to the API endpoint to retrieve
 * the history of agent assignments associated with the specified assignment ID.
 *
 * @async
 * @function getAssignmentHistory
 * @param {string} id - The unique identifier of the assignment.
 * @returns {Promise<Object>} A promise that resolves to the response data containing
 * the assignment history or rejects with an error if the request fails.
 */
export const getAssignmentHistory = async (id) => {
    return axios.get(
        `${config.API_BASE_URL}/api/v1/sims/agent-assignment-history?assignment_id=${id}`,
        getHeader(null, null)
    );
};

/**
 * Sends a state object containing notification data to the backend API to save a notification.
 *
 * @function SaveNotification
 * @async
 * @param {Object} state - The state object containing notification details to be saved.
 * @returns {Promise<Object>} A promise that resolves to the response data from the API.
 */
export const SaveNotification = async (state) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/users/save-notification`,
        state,
        getHeader(null, null),
    ).then((res) => res.data);
};

/**
 * Function to download notes in Excel format.
 *
 * @async
 * @function
 * @returns {Promise<AxiosResponse>} A promise resolving to the server response containing the Excel notes as an array buffer.
 * @description This function sends a GET request to the API endpoint to download user notes in Excel file format.
 * The request includes necessary headers and specifies the response type as 'arraybuffer' to handle binary data.
 */
export const downloadExcelNotes = async () => {
    return axios.get(
        `${config.API_BASE_URL}/api/v1/users/excel/download-notes`,
        {
            ...getHeader(),
            responseType: 'arraybuffer',
        }
    );
};

/**
 * Asynchronously recovers a SIM by making a POST request to the server.
 *
 * @param {string} id - The unique identifier of the SIM to be recovered.
 * @returns {Promise<Object>} A promise that resolves to the response of the recovery operation.
 * @throws {Error} Throws an error if the request fails.
 */
export const recoverSim = async (id) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/recover?sim_id=${id}`,
        {},
        getHeader(null, null)
    );
};

/**
 * Asynchronously sends a POST request to add captivate data associated with a specific simulation ID.
 *
 * @param {Object} formData - The form data to be sent in the request body.
 * @param {string | number} sim_id - The simulation ID to associate the captivate data with.
 * @returns {Promise<Object>} A promise that resolves to the response data from the server.
 * @throws {Error} Throws an error if the request fails.
 */
export const addCaptivate = async (formData, sim_id) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/add-captivate/${sim_id}`,
        formData,
        getHeader(null, null)
    ).then((res) => res.data);
};

/**
 * Asynchronous function to delete all items in the trash.
 *
 * This function sends a POST request to the server to delete all items stored in the trash.
 * It utilizes the base URL defined in the configuration and includes the necessary headers
 * for authentication or additional request information.
 *
 * @async
 * @function deleteAll
 * @returns {Promise} Resolves with the server's response after the request is processed.
 */
export const deleteAll = async () => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/trash`,
        {},
        getHeader(null, null)
    );
};

/**
 * Asynchronously sends a POST request to add a tutorial video for a specific simulation.
 *
 * @param {Object} formData - The data to be sent in the request body, typically representing the
 *      tutorial video information.
 * @param {string|number} sim_id - The unique identifier of the simulation to which the tutorial
 *      video will be added.
 * @returns {Promise<Object>} - A promise that resolves with the response data from the server.
 */
export const addTutorial = async (formData, sim_id) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/add-tutorial-video/${sim_id}`,
        formData,
        getHeader(null, null)
    ).then((res) => res.data);
};

/**
 * Asynchronously sends a POST request to add a Captivate tutorial video for the specified simulation.
 *
 * @param {FormData} formData - The form data containing the tutorial video information to be uploaded.
 * @param {string|number} sim_id - The unique identifier of the simulation to which the tutorial
 *      video will be added.
 * @returns {Promise<Object>} A promise that resolves to the response data from the API.
 */
export const addCaptivateTutorial = async (formData, sim_id) => {
    return axios.post(`${config.API_BASE_URL}/api/v1/sims/add-captivate-video/${sim_id}`,
        formData,
        getHeader(null, null))
        .then((res) => res.data);
};

/**
 * Removes uploaded media associated with a specific SIM based on the given parameters.
 * This function sends an asynchronous POST request to the API to remove media.
 *
 * @async
 * @function
 * @param {string} simId - The unique identifier of the SIM whose associated media is to be removed.
 * @param {string} filedType - The type of media or field to be removed.
 * @returns {Promise} - A promise that resolves to the response of the API request.
 */
export const removeUploads = async (simId, filedType) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/remove-media?sim_id=${simId}&field_type=${filedType}`,
        {},
        getHeader(null, null),
    );
};

/**
 * Checks the upload status of a video file by making an API request.
 *
 * @function
 * @param {string} fileId - The unique identifier of the file.
 * @param {number} size - The size of the file in bytes.
 * @returns {Promise<Object>} A promise that resolves to the response from the API containing the
 *      upload status.
 */
export const uploadStatus = (fileId, size) => {
    return axios.get(`${config.API_BASE_URL}/api/v1/sims/upload-video-status`, {
        headers: {
            Authorization: config.getToken(),
            task: null,
            type: null,
            'x-file-name': fileId,
            'file-size': size,
        },
    });
};

/**
 * Sends a HTTP POST request to notify the server that a video upload is complete.
 *
 * @param {string} fileId - The unique identifier for the uploaded file.
 * @param {string} id - The user response ID associated with the upload.
 * @returns {Promise} A promise representing the completion or failure of the POST request.
 */
export const uploadComplete = (fileId, id) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/upload-video-complete`,
        {
            userResponseId: id,
        },
        {
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
                'x-file-name': fileId,
            },
        }
    );
};

/**
 * Uploads a file chunk to the server.
 *
 * This function sends a POST request to upload a specific chunk of a file
 * to the designated API endpoint. It includes necessary headers for
 * identification and chunk-specific details.
 *
 * @param {Blob} chunk - The file chunk to be uploaded.
 * @param {string} fileId - The unique identifier for the file being uploaded.
 * @param {number} startChunk - The starting byte of the chunk within the file.
 * @param {number} endChunk - The ending byte of the chunk within the file.
 * @param {number} fileSize - The total size of the file being uploaded in bytes.
 * @returns {Promise} A promise that resolves with the server response or rejects with an error.
 */
export const uploadFiles = (chunk, fileId, startChunk, endChunk, fileSize) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/upload-video-files`,
        chunk,
        {
            headers: {
                Authorization: config.getToken(),
                task: null,
                type: null,
                'x-file-name': fileId,
                'Content-Range': `bytes ${startChunk}-${endChunk}/${fileSize}`,
                'file-size': fileSize,
            },
        }
    );
};

/**
 * Asynchronously merges audio files by sending a POST request to the audio merge API endpoint.
 *
 * This function takes audio-related data as input and sends the data to a specified API endpoint
 * for merging audio using the `axios` library. The request includes necessary headers.
 *
 * @function
 * @async
 *
 * @param {Object} data - The data payload containing information and/or audio files to be merged.
 *
 * @returns {Promise<Object>} A promise that resolves to the API response containing the result
 * of the audio merge operation.
 *
 * @throws {Error} Throws an error if the HTTP request fails or if the API encounters an issue.
 */
export const audioMerge = async (data) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/snowfly-audio-merge`, data,
        getHeader(null, null),
    );
};

/**
 * Sends an asynchronous POST request to remove the pass/fail status of a simulation.
 *
 * @param {string} sim_level_id - The ID representing the simulation level to be modified.
 * @param {string} sim_id - The unique identifier of the simulation to update.
 * @returns {Promise} - A Promise that resolves with the server's response after the request.
 */
export const removeSimPassFail = async (sim_level_id, sim_id) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/remove-sim-passFail?sim_level_id=${sim_level_id}&sim_id=${sim_id}`, {},
        getHeader(null, null),
    );
};

/**
 * Copies hint text from one level to another within a simulation.
 *
 * This function sends a POST request to the API to copy hint text from the specified source level
 * to the specified target level for a given simulation. It uses the provided simulation ID, source
 * level ID, and target level ID as parameters in the API URL.
 *
 * @param {string} sim_id - The unique identifier of the simulation.
 * @param {string} level_id - The identifier of the source level from which the hint text is copied.
 * @param {string} import_to_levelId - The identifier of the target level to which the hint text is copied.
 * @returns {Promise} A promise that resolves with the API response.
 */
export const copyHintTextFromLevelOne = async (sim_id, level_id, import_to_levelId) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/copy-hint-text?sim_id=${sim_id}&level_id=${level_id}&import_to_levelId=${import_to_levelId}`, {},
        getHeader(null, null),
    );
};

/**
 * Asynchronously saves input data for a simulation using provided simulation ID and user response ID.
 *
 * This function sends a POST request to the API endpoint responsible for saving simulation input details.
 * The request includes the simulation ID, user response ID, and input data, alongside the necessary headers.
 *
 * @param {string} simId - The unique identifier of the simulation.
 * @param {string} userResponseId - The unique identifier of the user's response.
 * @param {Object} data - The input data to be saved for the simulation.
 * @returns {Promise<Object>} A promise that resolves with the server response data.
 */
export const saveTakesimInput = async (simId, userResponseId, data) => {
    return axios
        .post(
            `${config.API_BASE_URL}/api/v1/sims/save-take-sim-input-details?simId=${simId}&userResponseId=${userResponseId}`,
            data,
            getHeader(null, null),
        )
        .then((res) => res.data);
};

/**
 * Uploads a keyword Excel file to the server for a specific SIM.
 *
 * @async
 * @function uploadKeywordExcel
 * @param {Object} data - The Excel file data to be uploaded.
 * @param {string} type - The type of data being uploaded (e.g., keyword category).
 * @param {string} simId - The unique identifier for the SIM associated with the upload.
 * @returns {Promise<Object>} A promise that resolves to the server's response.
 */
export const uploadKeywordExcel = async (data, type, simId) => {
    return axios.post(
        `${config.API_BASE_URL}/api/v1/sims/upload-keyword/${type}?sim_id=${simId}`, data,
        getHeader(null, null),
    );
};

/**
 * Uploads a file in chunks to the given url.
 *
 * @param url API endpoint where the file is to be uploaded to
 * @param file Actual File-like (Blob, File) to upload
 * @param fileType MIME type of the file
 *
 * @returns {Promise<unknown>}
 */
export const chunkedUpload = (url, file, fileType = null, fileName = null, cb = null) => {
    if (!file) {
        return Promise.reject(new Error("Please select a file to upload."));
    }

    const chunkSize = MAX_CHUNK_SIZE;
    const totalChunks = Math.ceil(file.size / chunkSize);
    let chunkNumber = 0;
    let fileSize = file.size;
    let start = 0;
    let end = Math.min(start + chunkSize, fileSize);

    const uploadNextChunk = () => {
        if (start >= fileSize) {
            return Promise.resolve({ success: true, complete: true });
        }

        const chunk = file.slice(start, end);
        const formData = new FormData();
        formData.append("chunkData", chunk);
        formData.append("chunkNumber", chunkNumber);
        formData.append("totalChunks", totalChunks);
        formData.append("originalname", file.name);

        if (fileName) formData.append('fileName', fileName);
        if (fileType) formData.append("fileType", fileType);

        return axios.post(url, formData, getHeader(null, null))
            .then(response => {
                const data = response ? response.data : null;

                if (cb) cb(formData, data ?? { success: false });

                if (!data || !data.success) {
                    throw new Error("Upload Failed");
                }

                chunkNumber++;
                start = end;
                end = Math.min(start + chunkSize, fileSize);

                if (chunkNumber < totalChunks && !data.complete) {
                    return uploadNextChunk();
                }

                return data;
            })
            .catch(err => {
                throw new Error("Error uploading chunk: " + err.toString());
            });
    };

    return uploadNextChunk();
};

/**
 * Uploads a tutorial video for a specific SIM by making a POST request to the relevant API endpoint.
 *
 * @async
 * @function uploadSIMTutorial
 * @param {string} simId - The unique identifier of the SIM for which the tutorial is being uploaded.
 * @param {string} downloadUrl - The URL where the tutorial video is hosted or can be downloaded from.
 * @param {string} fileName - The name of the tutorial video file being uploaded.
 * @returns {Promise<Object>} A promise that resolves to the response data from the API, or the full
 *      result if no data is available.
 * @throws {Error} Throws an error if the API request fails.
 */
export const uploadSIMTutorial = async (simId, downloadUrl, fileName) => {
    const url = `${config.API_BASE_URL}/api/v1/sims/add-tutorial-video/${simId}`;
    let headers = getHeader(null, null);
    headers['content-type'] = 'application/json; charset=UTF-8';

    let result = await axios.post(
        url,
        {
            'url': downloadUrl,
            'fileName': fileName,
        },
        headers
    );
    return result.data ?? result;
};

/**
 * Uploads an attachment related to a SIM to the server.
 *
 * @param {string} simId - The unique identifier of the SIM. If not provided, an empty string is
 *      used.
 * @param {string} downloadUrl - The URL of the file to be uploaded.
 * @param {string} fileName - The name of the file being uploaded.
 * @param {string} mimeType - The MIME type of the file.
 * @returns {Promise<Object>} A Promise resolving to the response from the server, containing
 *      information about the uploaded attachment.
 * @throws {Error} Throws an error if the request fails.
 */
export const uploadSIMAttachment = async (simId, downloadUrl, fileName, mimeType) => {
    const url = `${config.API_BASE_URL}/api/v1/sims/add-attachment`;

    let headers = getHeader(null, null);
    headers['content-type'] = 'application/json; charset=UTF-8';

    let result = await axios.post(
        url,
        {
            'id': simId ?? '',
            'url': downloadUrl,
            'fileName': fileName,
            'mimeType': mimeType
        },
        headers
    );

    return result.data ?? result;
};

/**
 * Retrieves a pre-signed AWS S3 URL for a file.
 * @param {string<'tutorial'|'attachment'|'logo'|'splash'>} itemType The type of object for which
 *      the URL is to be created
 * @param {string} itemName The name of the object for which the URL is to be created
 * @returns {Promise<any|AxiosResponse<any>>}
 */
export const getPresignedUrl = async (itemType, itemName) => {
    const url = `${config.API_BASE_URL}/api/v1/sims/get-presigned-url`;

    let headers = getHeader(null, null);
    headers['content-type'] = 'application/json; charset=UTF-8';

    let result = await axios.post(
        url,
        {
            'type': itemType,
            'fileName': itemName,
        },
        headers
    );

    return result.data ?? result;
};

/**
 * Uploads a file to the given AWS S3 pre-signed URL.
 * @param {string} url The pre-signed AWS S3 URL to upload the file to. It must be retrieved by
 *      calling {@link getPresignedUrl}
 * @param {File} file The file to upload.
 * @returns {Promise<{success: boolean}|{success: boolean, status: number, statusText: string}>}
 */
export const uploadToPresignedUrl = async (url, file) => {
    let data = new FormData();
    data.append('Key', file.name);
    data.append('Body', file);

    let result = await axios.put(
        url,
        file,
        {
            headers: {
                'Content-Type': '',
                'Accept': '*/*',
            }
        }
    );

    if (200 === result.status) {
        return {success: true};
    }

    return { success: false, status: result.status, statusText: result.statusText };
};

/**
 * Uploads a single one of the streams that compose a screen share video.
 *
 * @param file
 * @param fileType
 * @param fileName
 * @param cb
 * @returns {Promise<*>}
 */
export const uploadScreenShareStream = async (file, fileType, fileName, cb) => {
    const url = `${config.API_BASE_URL}/api/v1/sims/save-screen-share-stream`;
    return await chunkedUpload(url, file, fileType, fileName, cb);
};

/**
 * Retrieves a full video composed of the given media stream names. The names are returned by the
 *      {@link uploadScreenShareStream} function.
 *
 * @param sources Array of all the stream parts that compose the final Screen Sharing Recording.
 *      These are returned
 * by the backend when uploading each stream via {@link uploadScreenShareStream}
 * @param recordingLength The duration in milliseconds of the SIM (without Wrap Up)
 * @param stamps List of time stamps of every agent interaction with the SIM.
 * @param id The _id value of the user sim response that this screen sharing recording video will
 *      belong to.
 *
 * @returns {Promise<void>}
 */
export const requestScreenShareVideo = async (sources, recordingLength, stamps, id) => {
    const url = `${config.API_BASE_URL}/api/v1/sims/build-screen-share-video?id=${id}`;

    let headers = getHeader(null, null);
    headers['content-type'] = 'application/json; charset=UTF-8';

    let result =  await axios.post(
        url,
        {
            'sources': sources,
            'duration': recordingLength,
            'stamps': stamps
        },
        headers
    );

    return result.data ?? result;
};

/**
 * Asynchronously saves a screen share video to the server.
 *
 * @param {string} id - The unique identifier for the video session.
 * @param {string} videoUrl - The URL of the screen share video to be saved.
 * @param {boolean} [isBestPractice=false] - Optional flag indicating whether the video is tagged as
 *      best practice.
 * @returns {Promise<Object>} A promise that resolves to the response data from the server.
 * @throws {Error} Throws an error if the request fails.
 */
export const saveScreenShareVideo = async (id, videoUrl, isBestPractice = false) => {
    const url = `${config.API_BASE_URL}/api/v1/sims/save-screen-share-video?id=${id}`;

    let headers = getHeader(null, null);
    headers['content-type'] = 'application/json; charset=UTF-8';

    const data = {
        'url': videoUrl,
        'bestPractice': isBestPractice,
    };

    let result =  await axios.post(url, data, headers);

    return result.data ?? result;
};

/**
 * Asynchronously creates a new helpdesk ticket by sending the provided ticket data to the server.
 *
 * @param {Object} ticketData - The data for the ticket to be created.
 * @returns {Promise<Object>} A promise that resolves with the server response containing the
 *      created ticket data if the operation is successful, or the full response object otherwise.
 */
export const createHelpdeskTicket = async(ticketData) => {
    let result = await axios.post(
        `${config.API_BASE_URL}/api/v1/help-desk/tickets/add`,
        ticketData,
        getHeader(null, null)
    );

    if (result.status === 200) {
        return result.data;
    }

    return result;
};

/**
 * Adds a new entry to an existing helpdesk ticket.
 *
 * This function sends an asynchronous POST request to the helpdesk API to add
 * an entry or update associated information for a specified ticket.
 *
 * @param {string} ticketId - The unique identifier of the helpdesk ticket to which the entry will be added.
 * @param {Object} entry - The data or content of the entry to be added to the ticket.
 * @returns {Promise<Object>} Resolves to the response data from the API if the request is successful. Otherwise, returns the full response object.
 *
 * @throws {Error} If the API call fails or encounters an issue.
 */
export const addHelpdeskTicketEntry = async(ticketId, entry) => {
    let result = await axios.post(
        `${config.API_BASE_URL}/api/v1/help-desk/tickets/add-entry`,
        {
            ticket_id: ticketId,
            entry: entry
        },
        getHeader(null, null)
    );

    if (result.status === 200) {
        return result.data;
    }

    return result;
};

/**
 * Updates an existing helpdesk ticket with the provided data.
 *
 * Sends a POST request to the helpdesk API endpoint to update ticket details.
 * The function expects ticket data to be passed as an argument and includes
 * headers for authentication or additional configuration as required.
 *
 * @async
 * @function
 * @param {Object} ticketData - The data object containing ticket information to be updated.
 * @returns {Promise<Object>} - Returns a promise that resolves to the updated ticket data if the request is successful.
 * If the request fails, the full result from the API is returned.
 */
export const updateHelpdeskTicket = async(ticketData) => {
    let result = await axios.post(
        `${config.API_BASE_URL}/api/v1/help-desk/tickets/update`,
        ticketData,
        getHeader(null, null)
    );

    if (result.status === 200) {
        return result.data;
    }

    return result;
};

/**
 * Adds an attachment to a helpdesk ticket.
 *
 * This function sends an asynchronous POST request to the helpdesk API to add
 * an attachment to the specified ticket.
 *
 * @param {string} ticketId - The ID of the helpdesk ticket to which the attachment will be added.
 * @param {Object} attachment - The attachment data to be added to the ticket.
 * @returns {Promise<Object>} A promise that resolves to the server's response data if the request is successful.
 */
export const addHelpdeskTicketAttachment = async(ticketId, attachment) => {
    let result = await axios.post(
        `${config.API_BASE_URL}/api/v1/help-desk/tickets/attachments/add`,
        {
            ticket_id: ticketId,
            attachment: attachment
        },
        getHeader(null, null)
    );

    if (result.status === 200) {
        return result.data;
    }

    return result;
};

/**
 * Deletes an attachment from a specified helpdesk ticket.
 *
 * @async
 * @function deleteHelpdeskTicketAttachment
 * @param {string} ticketId - The unique identifier of the helpdesk ticket.
 * @param {Object} attachment - The attachment object to be deleted.
 * @returns {Promise<Object>} A promise that resolves to the response of the delete action. If the
 *      operation is successful, the resolved object typically contains the confirmation data.
 * @throws Will throw an error if the request fails or returns a non-200 status code.
 */
export const deleteHelpdeskTicketAttachment = async(ticketId, attachment) => {
    let result = await axios.post(
        `${config.API_BASE_URL}/api/v1/help-desk/tickets/attachments/delete`,
        {
            ticket_id: ticketId,
            attachment: attachment
        },
        getHeader(null, null)
    );

    if (result.status === 200) {
        return result.data;
    }

    return result;
};

/**
 * Closes a helpdesk ticket by sending a request to the API endpoint.
 *
 * @async
 * @function
 * @param {string} ticketId - The unique identifier of the ticket to be closed.
 * @param {string} status - The new status for the ticket, defaults to 'RESOLVED' if not provided.
 * @param {string} [message=''] - An optional message to include in the closure log.
 * @returns {Promise<Object>} A promise that resolves to the API response object containing the
 *      result of the operation.
 * @throws {Error} Throws an error if the API request fails or does not return a status of 200.
 */
export const closeHelpdeskTicket = async (ticketId, status, message = '') => {
    let result = await axios.post(
        `${config.API_BASE_URL}/api/v1/help-desk/tickets/close`,
        {
            ticket_id: ticketId,
            status: (status ?? 'RESOLVED'),
            entry: {
                action: 'close',
                message: message,
            },
        },
        getHeader(null, null)
    );

    if (result.status === 200) {
        return result.data;
    }

    return result;
};

/**
 * Deletes a specific helpdesk ticket by sending a POST request to the predefined API endpoint.
 *
 * @async
 * @function deleteHelpdeskTicket
 * @param {string} ticketId - The unique identifier of the helpdesk ticket to be deleted.
 * @param {string} status - The current status of the helpdesk ticket.
 * @param {string} [message=''] - An optional message describing the operation or additional context.
 * @returns {Promise<Object>} The API response data if the operation is successful; otherwise, the
 *      full result object.
 * @throws Will throw an error if the API request fails.
 */
export const deleteHelpdeskTicket = async (ticketId, status, message = '') => {
    let result = await axios.post(
        `${config.API_BASE_URL}/api/v1/help-desk/tickets/delete`,
        {
            ticket_id: ticketId,
            status: status,
            message: message,
        },
        getHeader(helpdesk.tickets.delete.task, helpdesk.tickets.delete.type)
    );

    if (result.status === 200) {
        return result.data;
    }

    return result;
};

/**
 * Fetches the details of a video by its unique identifier.
 *
 * @function fetchVideoDetails
 * @async
 * @param {string} id - The unique identifier of the video to retrieve details for.
 * @returns {Promise<Object>} A promise that resolves to the response containing the video details,
 *      or rejects with an error if the request fails.
 */
export const fetchVideoDetails = async (id) => {
    return await axios.get(
        `${config.API_BASE_URL}/api/v1/sims/get-video?id=${id}`,
        getHeader(null, null)
    );
};

/**
 * Fetches branding information, either from local storage (if available)
 *      or from a remote API endpoint. If data is retrieved from the API, it is
 *      cached in local storage for future use.
 *
 * Data is returned as a JavaScript object if available, or null/undefined
 *      otherwise.
 *
 * @async
 * @function fetchBranding
 * @returns {Promise<Object>} A promise that resolves to the branding data.
 *
 */
export const fetchBranding = async () => {
    const MAX_BRANDING_CHECKS = 20;
    let brandingStr = localStorage.getItem('branding');
    if (brandingStr) {
        let branding = JSON.parse(brandingStr);
        if (branding.counter < MAX_BRANDING_CHECKS) {
            branding.counter++;
            localStorage.setItem('branding', JSON.stringify(branding));
            return branding;
        }
    }

    const url = `${config.API_BASE_URL}/api/v1/login/fetch-branding`;

    let headers = getHeader(null, null);
    headers['content-type'] = 'application/json; charset=UTF-8';

    let result = await axios.get(
        url,
        { },
        headers
    );

    localStorage.setItem('branding', JSON.stringify({
        ...result.data, counter: 0
    }) ?? null);

    return result.data ?? result;
};

/**
 * Fetches a list of companies from the API.
 *
 * This asynchronous function makes a GET request to the company list endpoint
 * using Axios and retrieves data in JSON format. The API base URL is fetched
 * from the configuration file. The request includes a header specifying the
 * content type as JSON.
 *
 * @function
 * @async
 * @returns {Promise<Object>} Resolves to the company list data fetched from the API.
 */
export const fetchCompanyList = async() => {
    const url = `${config.API_BASE_URL}/api/v1/company/company-list`;

    let headers = getHeader(null, null);
    headers['content-type'] = 'application/json; charset=UTF-8';

    let result = await axios.get(
        url,
        { },
        headers
    );

    return result.data ?? result;
};

/**
 * Asynchronously sends a POST request to create a new company.
 *
 * @param {Object} data - The data object containing company information for the new Company.
 * @returns {Promise<Object>} A promise that resolves to the response data from the API.
 * @throws Will throw an error if the POST request fails.
 */
export const addCompany = async (data) => {
    let result = await axios.post(
        `${config.API_BASE_URL}/api/v1/company/update`,
        data,
        getHeader(null, null)
    );

    return result.data ?? result;
};

/**
 * Asynchronously updates a company's data by sending a PUT request to the API.
 *
 * @function
 * @async
 * @param {Object} data - The payload containing company information to be updated.
 * @returns {Promise<Object>} A promise resolving to the update result data from the API response,
 *      or the full response if result data is unavailable.
 */
export const saveCompany = async (data) => {
    let result;
    try {
        result = await axios.put(
            `${config.API_BASE_URL}/api/v1/company/update`,
            data,
            getHeader(null, null)
        );
    } catch (err) {
        result = {
            success: false,
            message: err.response?.data?.message ?? err?.message ?? err.toString(),
        };
    }

    return result.data ?? result;
};

/**
 * Asks the API to fetch an AWS S3 pre-signed URL for an audio recording.
 * This is needed when the browser denies the access to the audio file
 * due to CORS (Cross-Origin Resource Sharing) restrictions.
 *
 * Encodes the provided URL into a Base64 format before making
 * a GET request to the API endpoint. The function configures
 * the request with the appropriate headers and expects the
 * response to be in binary format (blob). Returns the audio
 * data if available, otherwise the entire response object.
 *
 * @param {string} url - The URL of the audio file to fetch.
 * @returns {Promise<Blob|Object>} A promise that resolves to the audio data (Blob)
 * if available, or the full response object.
 * @throws {Error} Throws an error if the request fails.
 */
export const fetchAudioByUrl = async (url) => {
    let encodedUrl = encodeURIComponent(url);
    let result = await axios.get(
        `${config.API_BASE_URL}/api/v1/sims/fetch-audio?url=${encodedUrl}`,
        {
            ...getHeader(null, null),
            responseType: 'blob'
        }
    );

    return result.data ?? result;
}
